import type { TextInputButtonSize } from '@stenajs-webui/elements';
import { type FC, type ReactNode, useState } from 'react';
import { stopPropagation } from '../../../common/eventHelpers';
import type { BasicBooking_importReference_status } from '../../../common/graphql/fragments/gql/BasicBooking';
import { getInformationMessageInfo } from '../../../common-ui/information-message/informationMessageUtils';
import { FreightPortalControlledPopover } from '../../../common-ui/popover/FreightPortalControlledPopover';
import { ImportExportStatusCode } from '../../../gql/graphql';
import { resolveInformationMessageType } from '../util/InformationMessageFactory';
import { ImportExportStatusIcon, TextInputButtonPlaceholder } from './ImportExportStatusIcon';
import { ImportExportPopupContent } from './popup/ImportExportPopupContent';

interface ImportExportStatusContentProps {
  bookingNo: number;
  children?: ReactNode;
  hideEmptyIcon?: boolean;
  onOpen: () => void;
  onRefreshIconClick?: () => Promise<void>;
  onRequestClose: () => void;
  showPopupRefreshStatusButton?: boolean;
  size?: TextInputButtonSize;
  status: BasicBooking_importReference_status | undefined | null;
  visible: boolean;
}

export const ImportExportStatusCellIconButton: FC<ImportExportStatusContentProps> = ({
  status,
  onRefreshIconClick,
  bookingNo,
  showPopupRefreshStatusButton = true,
  onOpen,
  onRequestClose,
  visible,
  size = 'medium',
  children,
  hideEmptyIcon = false,
}) => {
  const [loading, setLoading] = useState(false);

  if (hideEmptyIcon && !status) {
    return null;
  }

  if (!status) {
    return <TextInputButtonPlaceholder size={size} />;
  }

  const { statusCode } = status;

  if (statusCode === ImportExportStatusCode.Refresh && onRefreshIconClick) {
    const onClick = async () => {
      try {
        setLoading(true);
        await onRefreshIconClick();
      } finally {
        setLoading(false);
      }
    };
    return (
      <ImportExportStatusIcon
        spin={loading}
        code={statusCode}
        size={size}
        onClick={onClick}
        title={status.title}
      />
    );
  }

  const infoMessageIconProps = getInformationMessageInfo(resolveInformationMessageType(statusCode));

  return (
    <FreightPortalControlledPopover
      renderTrigger={props => {
        return (
          <ImportExportStatusIcon
            code={statusCode}
            size={size}
            onClick={onOpen}
            title={status.title}
            {...props}
          />
        );
      }}
      onRequestClose={onRequestClose}
      open={visible}
      placement={'left-start'}
    >
      <ImportExportPopupContent
        onDoubleClick={stopPropagation}
        onRequestClose={onRequestClose}
        status={status}
        infoMessageIconProps={infoMessageIconProps}
        bookingNo={bookingNo}
        showRefreshStatusButton={showPopupRefreshStatusButton}
      >
        {children}
      </ImportExportPopupContent>
    </FreightPortalControlledPopover>
  );
};
